/**
 * A room's ground tone: how damp and how sun-dried its grass is, cell by cell, for the client to tint the ground
 * with (rendering/impl/pixijs/ground_tone.ts). Sent as the room property `groundTone` (TileMapDirectory.groundTone).
 *
 * Rows cover the room and `margin` cells past every edge, so the tint blends across a seamless room's seam. Each
 * cell is two characters of TONE_DIGITS, damp then sun, each 0..63 for 0..1; "00" is the ground's own colour.
 */
export interface GroundTone {
    margin: number
    rows: string[]
}

export const TONE_DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_"
const TOP = TONE_DIGITS.length - 1

export const encodeToneCell = (damp: number, sun: number): string =>
    TONE_DIGITS[Math.round(Math.max(0, Math.min(1, damp)) * TOP)] + TONE_DIGITS[Math.round(Math.max(0, Math.min(1, sun)) * TOP)]

/** Cell (x, y) of the room, edges clamped: margin cells and beyond read the nearest row and column there is. */
export const groundToneCell = (tone: GroundTone, x: number, y: number): { damp: number; sun: number } => {
    const row = tone.rows[Math.max(0, Math.min(tone.rows.length - 1, y + tone.margin))] ?? ""
    const i = Math.max(0, Math.min(row.length / 2 - 1, x + tone.margin)) * 2
    return { damp: Math.max(0, TONE_DIGITS.indexOf(row[i])) / TOP, sun: Math.max(0, TONE_DIGITS.indexOf(row[i + 1])) / TOP }
}
